import { NgFor, NgIf } from '@angular/common';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    CUSTOM_ELEMENTS_SCHEMA,
    EventEmitter,
    Input,
    Output,
} from '@angular/core';
import { describeAccept, formatBytes, validateFile } from '../file-validation';

/** General file picker: drop zone plus the picked files as removable rows. Hands over File[]; never uploads. */
@Component({
    selector: 'app-file-upload',
    templateUrl: './file-upload.component.html',
    standalone: true,
    imports: [NgIf, NgFor],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    changeDetection: ChangeDetectionStrategy.OnPush,
    styles: [':host { display: block; }'],
})
export class FileUploadComponent {
    @Input() accept: string | null = null;
    @Input() multiple = false;
    @Input() maxBytes: number | null = null;
    @Input() maxFiles: number | null = null;
    @Input() hint: string | null = null;
    @Input() heading: string | null = null;
    @Input() disabled = false;

    @Input() set files(value: File[] | null) {
        const next = value ? [...value] : [];
        const same = next.length === this.selected.length && next.every((file, i) => file === this.selected[i]);
        if (!same) {
            this.selected = next;
            this.errors = [];
        }
    }

    @Output() filesChange = new EventEmitter<File[]>();

    selected: File[] = [];
    errors: string[] = [];

    constructor(private _cdr: ChangeDetectorRef) {}

    get shownHint(): string {
        if (this.hint !== null) {
            return this.hint;
        }
        const formats = describeAccept(this.accept);
        const limit = this.maxBytes ? `up to ${formatBytes(this.maxBytes)}${this.multiple ? ' each' : ''}` : '';
        return [formats, limit].filter(Boolean).join(', ');
    }

    get dropHeading(): string {
        return this.heading || (this.multiple ? 'Drag and drop files here' : 'Drag and drop a file here');
    }

    get canAddMore(): boolean {
        if (!this.multiple) {
            return this.selected.length === 0;
        }
        return !this.maxFiles || this.selected.length < this.maxFiles;
    }

    trackByIndex(index: number): number {
        return index;
    }

    size(file: File): string {
        return formatBytes(file.size);
    }

    onFiles(event: Event): void {
        const picked = (event as CustomEvent<File[]>).detail || [];
        if (!picked.length || this.disabled) {
            return;
        }
        const results = picked.map(file => ({ file, error: validateFile(file, this.accept, this.maxBytes) }));
        const accepted = results.filter(r => !r.error).map(r => r.file);
        let errors: string[] = results.filter(r => r.error).map(r => r.error as string);
        let next = this.multiple ? [...this.selected, ...accepted] : accepted.slice(0, 1);
        if (this.multiple && this.maxFiles && next.length > this.maxFiles) {
            errors = [...errors, `Up to ${this.maxFiles} files. ${next.length - this.maxFiles} not added.`];
            next = next.slice(0, this.maxFiles);
        }
        if (!this.multiple && !next.length) {
            next = this.selected;
        }
        this.errors = errors;
        this.update(next);
    }

    remove(index: number): void {
        if (this.disabled) {
            return;
        }
        this.errors = [];
        this.update(this.selected.filter((_, i) => i !== index));
    }

    clear(): void {
        this.errors = [];
        this.update([]);
    }

    private update(next: File[]): void {
        this.selected = next;
        this.filesChange.emit([...next]);
        this._cdr.markForCheck();
    }
}
